import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaMagnifyingGlassChart,
  FaArrowRight,
  FaArrowUpRightFromSquare,
} from "react-icons/fa6";
import PageHero from "../components/PageHero";

export default function Tools() {
  useEffect(() => {
    document.title = 'Tools — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Tools · In Development"
        title={
          <>
            State-of-the-art solutions{" "}
            <span className="italic text-brand-deep">on the way.</span>
          </>
        }
        lead="A small, focused suite of cloud tools that extend our services into the everyday work of researchers and accreditation teams. These tools are planned and in development — not yet launched."
      />

      {/* In-development tools */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ToolCard
            icon={<FaMagnifyingGlassChart />}
            name="Research Data Analyzer"
            tagline="AI-driven analytics for researchers."
            desc="An AI-driven analytics platform designed to help researchers process, analyze, and visualize complex datasets with greater ease, clarity, and efficiency — built around real research workflows, not generic dashboards."
            features={[
              "Quantitative + qualitative analysis",
              "Reproducible analysis pipelines",
              "Publication-ready visualizations",
              "Designed for academic use",
            ]}
          />
          <article className="rounded-3xl border border-dashed border-ink/20 bg-cream/60 p-8 sm:p-10 flex flex-col justify-between relative overflow-hidden">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep">
                More tools
              </div>
              <h3 className="mt-3 font-display text-3xl text-ink leading-tight">
                Additional tools{" "}
                <span className="italic text-ink/50">in development.</span>
              </h3>
              <p className="mt-4 text-slate2 leading-relaxed">
                We are developing new tools to support researchers, educators, and
                institutions. We will share more as each becomes available.
              </p>
            </div>
            <Link to="/contact" className="group btn-link mt-7 w-fit">
              Get notified{" "}
              <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
            </Link>
          </article>
        </div>
      </section>

      {/* Roadmap note */}
      <section className="bg-cream">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="eyebrow eyebrow-bar">Roadmap</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                Built deliberately. Released when ready.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate2 leading-relaxed">
                Our tools are shaped by the work we already do with researchers and
                institutions. We invite early collaborators into design conversations
                before we ship — so the tools fit how the work actually happens.
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-ink/10 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink/10">
              {[
                { phase: "Now", t: "Discovery & design", d: "Working with researchers and accreditation leads to validate workflows." },
                { phase: "Next", t: "Closed pilots", d: "Early access for selected partners to test in real engagements." },
                { phase: "Later", t: "Public availability", d: "Broader release after pilots prove out reliability and value." },
              ].map((p) => (
                <div key={p.phase} className="p-7">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep">
                    {p.phase}
                  </div>
                  <h3 className="mt-3 font-display text-xl text-ink">{p.t}</h3>
                  <p className="mt-2 text-slate2 leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bone">
        <div className="container-page pb-20 pt-16">
          <div className="rounded-3xl bg-ink text-bone p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Early access
              </div>
              <h3 className="mt-3 font-display text-2xl lg:text-3xl leading-tight">
                Want to be notified — or to help shape these tools?
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
            >
              Join the early list <FaArrowUpRightFromSquare className="text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const ToolCard = ({ icon, name, tagline, desc, features }) => (
  <article className="bg-white rounded-3xl border border-ink/10 p-8 sm:p-10 flex flex-col relative overflow-hidden">
    <div className="absolute top-6 right-6 inline-flex items-center gap-2 rounded-full bg-brand-sage/30 text-brand-deep text-[11px] tracking-[0.16em] uppercase px-3 py-1">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-deep" />
      In development
    </div>
    <div className="text-3xl text-brand-deep">{icon}</div>
    <h3 className="mt-6 font-display text-3xl text-ink leading-tight">{name}</h3>
    <p className="mt-2 font-display italic text-slate2">{tagline}</p>
    <p className="mt-4 text-slate2 leading-relaxed">{desc}</p>

    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      {features.map((f) => (
        <li
          key={f}
          className="rounded-lg border border-ink/10 px-3 py-2 text-ink/80 bg-bone"
        >
          {f}
        </li>
      ))}
    </ul>

    <Link to="/contact" className="group btn-link mt-7 w-fit">
      Get notified <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
    </Link>
  </article>
);
